import { CURRENCIES, RATE_BASES } from '@chukua/common';
import { Alert, Button, DatePicker, Drawer, Form, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { useSubmission } from '../../../../../shared/hooks/use-submission';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { ZoneSearchSelect } from '../../../../zones/public/ZoneSearchSelect';
import { RATE_BASIS_LABELS } from '../../../../../shared/policies/rate-basis-labels';
import { AmountFields } from '../../../components/AmountFields';
import { toRateLineInput, type RateLineFormValues } from '../../../policies/build-rate-line-input';
import { CargoKeyFields } from './CargoKeyFields';
import { createRateLine } from '../../../rate-cards.api';
import { failureMessageFor } from '../../../policies/rate-line-failure-message';
import { useRateLineCatalogues } from '../hooks/use-rate-line-catalogues';

const BASIS_OPTIONS = RATE_BASES.map((basis) => ({ value: basis, label: RATE_BASIS_LABELS[basis] }));

interface AddLineDrawerProps {
  open: boolean;
  rateCardId: string;
  onClose: () => void;
}

/** A new price for a key the card doesn't have yet. It opens a draft proposal: nothing is priced from it until it is submitted and approved. */
export function AddLineDrawer({ open, rateCardId, onClose }: AddLineDrawerProps) {
  const [form] = Form.useForm<RateLineFormValues>();
  const catalogues = useRateLineCatalogues(open);
  const { error, submitting, submit, clearError } = useSubmission();
  const toast = useToast();

  async function create(values: RateLineFormValues) {
    const created = await submit(() => createRateLine(rateCardId, toRateLineInput(values)).then(() => true), failureMessageFor);
    if (!created) return;
    toast.success('Draft proposal created — submit it for approval from the Proposals tab');
    form.resetFields();
    onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} afterOpenChange={clearError} title="Add rate line" placement="right" size={520} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<RateLineFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={create}
        disabled={submitting}
        initialValues={{ cargoKind: 'container', basis: 'per_container', currency: CURRENCIES[0] }}
      >
        <div className="grid grid-cols-2 gap-3">
          <Form.Item name="originZoneCode" label="Origin" rules={[{ required: true, message: 'Choose an origin' }]}>
            <ZoneSearchSelect />
          </Form.Item>
          <Form.Item name="destinationZoneCode" label="Destination" rules={[{ required: true, message: 'Choose a destination' }]}>
            <ZoneSearchSelect />
          </Form.Item>
        </div>
        <Form.Item name="jobTypeCode" label="Job type" rules={[{ required: true, message: 'Choose a job type' }]}>
          <Select options={catalogues.jobTypes.map((type) => ({ value: type.code, label: type.name }))} />
        </Form.Item>
        <CargoKeyFields catalogues={catalogues} />
        <Form.Item name="basis" label="Priced per" rules={[{ required: true }]}>
          <Select options={BASIS_OPTIONS} />
        </Form.Item>
        <AmountFields />
        <div className="grid grid-cols-2 gap-3">
          <Form.Item name="validFrom" label="Effective from" extra="Must be after today to be approved" rules={[{ required: true, message: 'Choose a date' }]}>
            <DatePicker className="w-full" disabledDate={(day) => !day.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="validTo" label="Until (optional)">
            <DatePicker className="w-full" />
          </Form.Item>
        </div>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, whitespace: true, message: 'Say why' }]}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting}>
          Save as draft
        </Button>
      </Form>
    </Drawer>
  );
}
